import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "@/components/ui/form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Review } from "@/types/review";
import { useLanguage } from "@/context/LanguageContext";
import { translateComment } from "@/utils/translateComment";
import { createReviewFormSchema } from "./form/formSchema";
import { processPrivacyAddress } from "@/utils/addressUtils";

// Import refactored components
import { CountryRegionFields } from "./form/CountryRegionFields";
import { AddressValidation } from "./form/AddressValidation";
import { BasicFields } from "./form/BasicFields";
import { LocationFields, RentFields, OptionsFields } from "./form/FormFields";
import { ImmigrantFriendlyFields, PetFriendlyFields, ApplicationEaseFields, ContractFlexibilityFields } from "./form/FormFields";
import { ReviewFormSubmit } from "./form/ReviewFormSubmit";
import { RatingField } from "./form/RatingField";
import { PeriodFields } from "./form/PeriodFields";
import { ImageUpload } from "./form/ImageUpload";
import { PrivacyFields } from "./form/PrivacyFields";

const ReviewForm = ({ 
  initialValues, 
  isEditing = false, 
  reviewId, 
  userId 
}: { 
  initialValues?: Review; 
  isEditing?: boolean; 
  reviewId?: string; 
  userId?: string | null;
}) => {
  const { t, language } = useLanguage();
  
  const formSchema = createReviewFormSchema(language);

  const [selectedCountry, setSelectedCountry] = useState<string>(initialValues?.country || 'CA');
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressValidated, setAddressValidated] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any | null>(null);

  const documentOptions = [
    { value: 'income', label: language === 'en' ? 'Income Proof' : '薪資單' },
    { value: 'savings', label: language === 'en' ? 'Savings Proof' : '存款證明' },
    { value: 'credit', label: language === 'en' ? 'Credit Score' : '信用分數' },
    { value: 'history', label: language === 'en' ? 'Rental History' : '租房經歷' },
    { value: 'other', label: language === 'en' ? 'Other' : '其他' },
  ];

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      community_name: initialValues?.community_name || "",
      address_detail: initialValues?.address_detail || "",
      country: initialValues?.country || 'CA',
      region: initialValues?.region || '',
      rental_status: initialValues?.rental_status || 'viewed',
      housing_type: initialValues?.housing_type || undefined,
      bedrooms: initialValues?.bedrooms || 0,
      living_rooms: initialValues?.living_rooms || 0,
      bathrooms: initialValues?.bathrooms || 0,
      rental_year_start: initialValues?.rental_year_start || new Date().getFullYear(),
      rental_year_end: initialValues?.rental_year_end,
      rating: initialValues?.rating || 3,
      comment: initialValues?.comment || "",
      rent: initialValues?.rent || "",
      includes_utilities: initialValues?.includes_utilities || false,
      utilities_included: initialValues?.utilities_included || [],
      
      // Privacy control fields
      show_landlord_name: initialValues?.show_landlord_name !== false ? true : false,
      hide_street_number: initialValues?.hide_street_number || false,
      
      // Immigrant-friendly details
      accepts_overseas_proof_of_funds: initialValues?.accepts_overseas_proof_of_funds || false,
      accepts_overseas_employment_proof: initialValues?.accepts_overseas_employment_proof || false,
      immigrant_friendly: initialValues?.immigrant_friendly || false,
      
      // Pet-friendly details
      pet_allowed: initialValues?.pet_allowed || false,
      pet_fee: initialValues?.pet_fee || false,
      no_pet_limit_count: initialValues?.no_pet_limit_count || false,
      no_pet_limit_type: initialValues?.no_pet_limit_type || false,
      
      // Application ease
      requires_employment_proof: initialValues?.requires_employment_proof || false,
      requires_credit_check: initialValues?.requires_credit_check || false,
      has_income_requirement: initialValues?.has_income_requirement || false,
      requires_financial_proof: initialValues?.requires_financial_proof || false,
      no_proof_required: initialValues?.no_proof_required || false,
      
      // Contract flexibility
      allows_daily_rental: initialValues?.allows_daily_rental || false,
      allows_monthly_rental: initialValues?.allows_monthly_rental || false,
      allows_semiannual_rental: initialValues?.allows_semiannual_rental || false,
      allows_contract_termination: initialValues?.allows_contract_termination || false,
      allows_contract_transfer: initialValues?.allows_contract_transfer || false,
      
      deposit_type: initialValues?.deposit_type || "months",
      deposit_value: initialValues?.deposit_value || "",
      min_rental_period: initialValues?.min_rental_period,
      notice_period: initialValues?.notice_period,
      viewing_during_lease: initialValues?.viewing_during_lease || false,
      required_documents: initialValues?.required_documents || [],
      image_urls: initialValues?.image_urls || [],
      
      // Parking information
      parking_information: initialValues?.parking_information || [],
    },
  });

  const onSubmit = async (values: any) => {
    console.log("🔥🔥🔥 FORM SUBMIT TRIGGERED!", { 
      isEditing, 
      reviewId, 
      addressValidated, 
      formValues: values,
      timestamp: new Date().toISOString()
    });
    
    // 檢查表單驗證狀態
    const formState = form.formState;
    console.log("🔥 Form validation state:", {
      isValid: formState.isValid,
      errors: formState.errors,
      isDirty: formState.isDirty,
      dirtyFields: formState.dirtyFields
    });
    
    // 編輯模式完全跳過地址驗證
    if (isEditing) {
      console.log("✅ Editing mode: Skipping address validation completely");
      if (!selectedLocation && initialValues?.coordinates) {
        setSelectedLocation(initialValues.coordinates);
      }
    } else if (!addressValidated) {
      console.error("❌ Address validation required for new reviews");
      toast({
        variant: "destructive",
        title: language === 'en' ? "Please validate address" : "請驗證地址",
        description: language === 'en' ? "Please select a valid address from the suggestions" : "請從建議中選擇有效地址",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log("🔐 Checking authentication...");
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error("❌ Authentication error:", authError);
        toast({
          variant: "destructive",
          title: language === 'en' ? "Authentication Error" : "認證錯誤",
          description: authError.message,
        });
        return;
      }
      
      if (!user) {
        console.error("❌ User not authenticated");
        toast({
          variant: "destructive",
          title: language === 'en' ? "Please log in" : "請先登入",
          description: language === 'en' ? "You need to be logged in to submit a review" : "您需要登入才能提交評價",
        });
        return;
      }

      console.log("✅ User authenticated:", user.id);

      // 處理地址和座標
      const fullCoordinates = {
        lat: parseFloat(String(selectedLocation.lat)),
        lng: parseFloat(String(selectedLocation.lng))
      };

      console.log("🔥 Processing coordinates:", fullCoordinates);

      // 處理隱私地址（僅在新建評論時處理）
      let privacyResult;
      if (!isEditing) {
        console.log("🔥 Processing privacy address:", {
          address: values.address_detail,
          hideStreetNumber: values.hide_street_number,
          showLandlordName: values.show_landlord_name
        });
        
        try {
          privacyResult = await processPrivacyAddress(
            values.address_detail,
            fullCoordinates,
            values.hide_street_number
          );
          console.log("✅ Privacy result:", privacyResult);
        } catch (privacyError) {
          console.error("🔥 Privacy processing error:", privacyError);
          // 如果隱私處理失敗，使用原始地址
          privacyResult = {
            displayAddress: values.address_detail,
            displayCoordinates: fullCoordinates
          };
        }
      }

      // Update the immigrant_friendly field based on the detail fields
      values.immigrant_friendly = 
        values.accepts_overseas_proof_of_funds || 
        values.accepts_overseas_employment_proof;

      const reviewData: any = {
        user_id: user.id,
        community_name: values.community_name,
        landlord_name: values.landlord_name || null,
        rental_status: values.rental_status,
        housing_type: values.housing_type,
        
        // 只在新建評論時包含地址相關欄位
        ...(isEditing ? {} : {
          address_detail: values.address_detail,
          country: values.country,
          region: values.region,
          coordinates: fullCoordinates,
          show_landlord_name: values.show_landlord_name,
          hide_street_number: values.hide_street_number,
          address_full: values.address_detail,
          display_address: privacyResult?.displayAddress || values.address_detail,
          full_coordinates: fullCoordinates,
          display_coordinates: privacyResult?.displayCoordinates || fullCoordinates,
        }),
        
        bedrooms: values.bedrooms,
        living_rooms: values.living_rooms,
        bathrooms: values.bathrooms,
        rental_year_start: values.rental_year_start || null,
        rental_year_end: values.rental_year_end || null,
        rating: values.rating,
        comment: values.comment || null,
        rent: values.rent,
        includes_utilities: values.includes_utilities,
        utilities_included: values.utilities_included,
        
        accepts_overseas_proof_of_funds: values.accepts_overseas_proof_of_funds,
        accepts_overseas_employment_proof: values.accepts_overseas_employment_proof,
        immigrant_friendly: values.immigrant_friendly,
        
        pet_allowed: values.pet_allowed,
        pet_fee: values.pet_fee,
        no_pet_limit_count: values.no_pet_limit_count,
        no_pet_limit_type: values.no_pet_limit_type,
        
        requires_employment_proof: values.requires_employment_proof,
        requires_credit_check: values.requires_credit_check,
        has_income_requirement: values.has_income_requirement,
        requires_financial_proof: values.requires_financial_proof,
        no_proof_required: values.no_proof_required,
        
        allows_daily_rental: values.allows_daily_rental,
        allows_monthly_rental: values.allows_monthly_rental,
        allows_semiannual_rental: values.allows_semiannual_rental,
        allows_contract_termination: values.allows_contract_termination,
        allows_contract_transfer: values.allows_contract_transfer,
        
        deposit_type: values.deposit_type,
        deposit_value: values.deposit_value,
        min_rental_period: values.min_rental_period || null,
        notice_period: values.notice_period || null,
        viewing_during_lease: values.viewing_during_lease,
        required_documents: values.required_documents || [],
        image_urls: values.image_urls || [],
        
        parking_information: values.parking_information || [],
      };

      console.log("🔥 Review data prepared:", { isEditing, reviewId, dataKeys: Object.keys(reviewData) });

      // Translate comment if provided
      if (values.comment && values.comment.trim() !== '') {
        try {
          console.log("🔥 Translating comment:", values.comment);
          
          const translationData = await translateComment(values.comment);
          
          if (Object.keys(translationData).length > 0) {
            console.log("✅ Translation successful:", translationData);
            
            if (translationData['en']) {
              reviewData.comment_en = translationData['en'];
            }
            if (translationData['zh-TW']) {
              reviewData.comment_zh_tw = translationData['zh-TW'];
            }
            if (translationData['zh-CN']) {
              reviewData.comment_zh_cn = translationData['zh-CN'];
            }
            
            reviewData.comment_translations = translationData;
            reviewData.comment_language = ''; 
          }
        } catch (translateError) {
          console.error("🔥 Error translating comment:", translateError);
          // 翻譯失敗不影響主要提交流程
        }
      }

      console.log("🔥 Submitting to Supabase:", isEditing ? "UPDATE" : "INSERT");
      
      let response;
      
      if (isEditing && reviewId) {
        console.log(`🔥 Updating review ID: ${reviewId} for user: ${user.id}`);
        
        console.log('🔥 Updating review with data:', {
          reviewId,
          userId: user.id,
          dataKeys: Object.keys(reviewData),
          reviewData
        });
        
        response = await supabase
          .from('reviews')
          .update(reviewData)
          .eq('id', reviewId)
          .eq('user_id', user.id)
          .select();
          
        console.log("🔥 Update response:", response);
      } else {
        console.log("🔥 Creating new review");
        response = await supabase
          .from('reviews')
          .insert(reviewData);
          
        console.log("🔥 Insert response:", response);
      }

      const { error } = response;
      
      if (error) {
        console.error("🔥 Supabase error:", error);
        
        // 更詳細的錯誤訊息處理
        let errorMessage = error.message;
        if (error.code === 'PGRST116') {
          errorMessage = language === 'en' ? 'No record found to update. You may not have permission to edit this review.' : '找不到要更新的記錄。您可能沒有權限編輯此評論。';
        } else if (error.code === '23505') {
          errorMessage = language === 'en' ? 'Duplicate entry detected.' : '檢測到重複條目。';
        } else if (error.code === '23502') {
          errorMessage = language === 'en' ? 'Required field is missing.' : '缺少必填欄位。';
        }
        
        throw new Error(errorMessage);
      }
      
      console.log("✅ Submission successful!");
      
      toast({
        title: isEditing 
          ? (language === 'en' ? "Review Updated" : "評價更新成功") 
          : (language === 'en' ? "Review Submitted" : "評價提交成功"),
        description: isEditing 
          ? (language === 'en' ? "Your review has been updated successfully" : "您的租屋評價已成功更新")
          : (language === 'en' ? "Thank you for sharing your experience" : "感謝您分享租屋經驗，您的評價已成功上傳"),
      });
      
      if (isEditing) {
        navigate("/member-reviews");
      } else {
        form.reset();
        setAddressValidated(false);
        setSelectedLocation(null);
      }
    } catch (error: any) {
      console.error("🔥 Error submitting review:", error);
      
      // 顯示詳細錯誤訊息給用戶
      let userFriendlyMessage = error.message;
      
      if (error.message.includes('Failed to fetch')) {
        userFriendlyMessage = language === 'en' 
          ? 'Network connection failed. Please check your internet connection and try again.'
          : '網路連線失敗。請檢查您的網路連線後重試。';
      } else if (error.message.includes('JWT')) {
        userFriendlyMessage = language === 'en' 
          ? 'Session expired. Please refresh the page and login again.'
          : '登入已過期。請重新整理頁面並重新登入。';
      } else if (error.message.includes('permission')) {
        userFriendlyMessage = language === 'en' 
          ? 'You do not have permission to perform this action.'
          : '您沒有權限執行此操作。';
      }
      
      toast({
        variant: "destructive",
        title: isEditing 
          ? (language === 'en' ? "Failed to update review" : "評價更新失敗") 
          : (language === 'en' ? "Failed to submit review" : "評價提交失敗"),
        description: userFriendlyMessage,
      });
      
      // 也在控制台顯示完整錯誤以供調試
      console.error("🔥 Full error details:", {
        error: error,
        stack: error.stack,
        isEditing,
        reviewId,
        userId: userId
      });
    } finally {
      console.log("🔥 Setting isSubmitting to false");
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    console.log("🔥 ReviewForm: Initializing form with values:", { 
      hasCoordinates: !!(initialValues?.coordinates?.lat && initialValues?.coordinates?.lng),
      coordinates: initialValues?.coordinates,
      addressDetail: initialValues?.address_detail,
      isEditing
    });
    
    // 編輯模式下，總是設定地址驗證為通過，避免地址驗證阻擋提交
    if (isEditing) {
      console.log("🔥 ReviewForm: Editing mode - auto-validating address");
      setAddressValidated(true);
      
      if (initialValues?.coordinates?.lat && initialValues?.coordinates?.lng) {
        setSelectedLocation(initialValues.coordinates);
      } else {
        // 即使沒有座標，編輯模式下也設定預設座標
        setSelectedLocation({ lat: 51.0447, lng: -114.0719 });
      }
    } else if (initialValues?.coordinates?.lat && initialValues?.coordinates?.lng) {
      console.log("🔥 ReviewForm: New form with coordinates - setting validation");
      setAddressValidated(true);
      setSelectedLocation(initialValues.coordinates);
    }
  }, [initialValues, isEditing]);

  return (
    <Form {...form}>
      <form onSubmit={(e) => {
        console.log("🔥 Native form onSubmit triggered!", e);
        console.log("🔥 Form errors before submit:", form.formState.errors);
        console.log("🔥 Form values:", form.getValues());
        console.log("🔥 rental_year_end specific:", {
          value: form.getValues('rental_year_end'),
          error: form.formState.errors.rental_year_end
        });
        form.handleSubmit(onSubmit, (errors) => {
          console.log("🚨 Form validation failed:", errors);
        })(e);
      }} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-6 rounded-md border p-4">
          <h2 className="text-xl font-semibold">
            {language === 'en' ? 'Basic Information' : '基本資訊'}
          </h2>
          
          {/* Community Name / Landlord Name */}
          <BasicFields form={form} />
          
          {/* Country & Region */}
          <CountryRegionFields 
            form={form}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            setAddressValidated={setAddressValidated}
            setSelectedLocation={setSelectedLocation}
          />
  
          {/* Address */}
          <AddressValidation
            form={form}
            addressValidated={addressValidated}
            setAddressValidated={setAddressValidated}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            isEditing={isEditing}
            reviewId={reviewId}
          />
          
          {/* Privacy Settings */}
          <PrivacyFields form={form} />
          
          {/* Rental Status */}
          <FormField
            control={form.control}
            name="rental_status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {language === 'en' ? 'Rental Status' : '租屋狀態'}
                  <span className="text-red-500 ml-1">*</span>
                </FormLabel>
                <div className="grid grid-cols-3 gap-2">
                  <div className={`p-3 border rounded-lg cursor-pointer text-center text-sm ${field.value === 'viewed' ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}
                    onClick={() => field.onChange('viewed')}>
                    {language === 'en' ? 'Viewed' : '已看房'}
                  </div>
                  <div className={`p-3 border rounded-lg cursor-pointer text-center text-sm ${field.value === 'renting' ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}
                    onClick={() => field.onChange('renting')}>
                    {language === 'en' ? 'Renting' : '已承租'}
                  </div>
                  <div className={`p-3 border rounded-lg cursor-pointer text-center text-sm ${field.value === 'ended' ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}
                    onClick={() => field.onChange('ended')}>
                    {language === 'en' ? 'Ended' : '已結束租約'}
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {/* Housing Type and Room Details */}
          <LocationFields form={form} />
          
          {/* Rating */}
          <RatingField form={form} />
          
          {/* Comment */}
          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {language === 'en' ? 'Comment' : '評論'}
                </FormLabel>
                <FormControl>
                  <textarea
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder={language === 'en' ? "Please share your rental experience and feelings" : "請分享您的租屋經驗與感受"}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {/* Image Upload */}
          <ImageUpload form={form} />
        </div>

        {/* Rental Details/Fee Section */}
        <div className="space-y-6 rounded-md border p-4">
          <h2 className="text-xl font-semibold">
            {language === 'en' ? 'Fees' : '費用'}
          </h2>
          
          <RentFields form={form} />
        </div>
        
        {/* Conditions Section */}
        <div className="space-y-6 rounded-md border p-4">
          <h2 className="text-xl font-semibold">
            {language === 'en' ? 'Other Information' : '其他資訊'}
          </h2>
          
          {/* Pet Friendly Section */}
          <PetFriendlyFields form={form} />
          
          {/* Immigrant Friendly Section */}
          <ImmigrantFriendlyFields form={form} />
          
          {/* Application Ease Section */}
          <ApplicationEaseFields form={form} />
          
          {/* Contract Flexibility Section */}
          <ContractFlexibilityFields form={form} />
        </div>

        <ReviewFormSubmit 
          isSubmitting={isSubmitting} 
          addressValidated={addressValidated} 
          isEditing={isEditing} 
        />
      </form>
    </Form>
  );
};

export default ReviewForm;
